import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Bell,
  Box,
  Calculator,
  ClipboardList,
  FileText,
  Mail,
  Monitor,
  Percent,
  Settings as SettingsIcon,
  Shield,
  Sliders,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { ApiError, tenantApi } from '@/features/auth/api';
import { useAuthStore } from '@/stores/auth-store';
import { PageHeader } from '@/lib/ui';

const sections: { to: string; title: string; body: string; icon: LucideIcon }[] = [
  {
    to: '/settings/users',
    title: 'Team members',
    body: 'Invite teammates, manage pending invitations.',
    icon: Users,
  },
  {
    to: '/settings/roles',
    title: 'Roles & permissions',
    body: 'System roles + custom roles you clone.',
    icon: Shield,
  },
  {
    to: '/settings/asset-types',
    title: 'Asset types',
    body: 'Categories, icons, required documents, metadata schema.',
    icon: Box,
  },
  {
    to: '/settings/document-types',
    title: 'Document types',
    body: 'Slugs per scope, required flag, expiry lead-day ladder.',
    icon: FileText,
  },
  {
    to: '/settings/attendance',
    title: 'Attendance settings',
    body: 'Define shifts and overtime rules used by the attendance calendar + payroll.',
    icon: ClipboardList,
  },
  {
    to: '/settings/tenant-config',
    title: 'Tenant configuration',
    body: 'Twelve editable categories with versioned history + rollback.',
    icon: Sliders,
  },
  {
    to: '/settings/alert-rules',
    title: 'Alert rules',
    body: 'Enable/disable rules and tune severity + thresholds.',
    icon: Bell,
  },
  {
    to: '/settings/salary-formulas',
    title: 'Salary formulas',
    body: 'Edit + preview the payroll formula DSL.',
    icon: Calculator,
  },
  {
    to: '/settings/commission-rules',
    title: 'Commission rules',
    body: 'Flat, percent, or tiered accrual rules.',
    icon: Percent,
  },
  {
    to: '/settings/sessions',
    title: 'Active sessions',
    body: 'See where you are signed in and revoke devices.',
    icon: Monitor,
  },
  {
    to: '/settings/audit-log',
    title: 'Audit log',
    body: 'Append-only ledger of every business write — filter, page, export CSV.',
    icon: ClipboardList,
  },
  {
    to: '/settings/notifications',
    title: 'Notification preferences',
    body: 'Pick which categories reach you on which channel (email · push · in-app).',
    icon: Mail,
  },
];

export function SettingsPage() {
  const perms = useAuthStore.getState().tenant?.permissions ?? [];
  const canCancel = perms.includes('tenant.cancel');
  const canReactivate = perms.includes('tenant.update');

  const tenant = useQuery({ queryKey: ['tenant', 'current'], queryFn: tenantApi.current });
  const isSuspendedOrCancelled =
    tenant.data?.status === 'SUSPENDED' || tenant.data?.status === 'CANCELLED';

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-narrow space-y-8">
        <PageHeader
          title="Settings"
          icon={SettingsIcon}
          subtitle="Tenant configuration editor, alert rules, identity, and operations catalogues."
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {sections.map((s) => {
            const Icon = s.icon;
            return (
              <Link key={s.to} to={s.to} className="card-interactive group flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary ring-1 ring-primary/20 transition-colors group-hover:bg-primary/25">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <div className="font-semibold leading-tight text-foreground">{s.title}</div>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                </div>
              </Link>
            );
          })}
        </div>

        {(canCancel || (canReactivate && isSuspendedOrCancelled)) && tenant.data && (
          <TenantLifecycleSection
            tenantName={tenant.data.name}
            tenantStatus={tenant.data.status}
            canCancel={canCancel}
            canReactivate={canReactivate}
          />
        )}
      </div>
    </div>
  );
}

function TenantLifecycleSection({
  tenantName,
  tenantStatus,
  canCancel,
  canReactivate,
}: {
  tenantName: string;
  tenantStatus: string;
  canCancel: boolean;
  canReactivate: boolean;
}) {
  const [showCancel, setShowCancel] = useState(false);
  const queryClient = useQueryClient();

  const reactivate = useMutation({
    mutationFn: () => tenantApi.reactivateTenant(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant', 'current'] }),
  });

  const inactive = tenantStatus === 'SUSPENDED' || tenantStatus === 'CANCELLED';

  return (
    <section className="card border-danger/40">
      <h2 className="text-lg font-semibold text-danger">Danger zone</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Tenant lifecycle controls. Cancelling locks every member out within a minute.
      </p>

      <div className="mt-4 flex items-center justify-between rounded-md border border-border bg-background/40 p-3">
        <div>
          <div className="text-sm font-medium">Workspace status</div>
          <p className="text-xs text-muted-foreground">
            Current state: <span className="font-mono">{tenantStatus}</span>
          </p>
        </div>
        {inactive
          ? canReactivate && (
              <button
                onClick={() => reactivate.mutate()}
                disabled={reactivate.isPending}
                className="btn-primary"
              >
                {reactivate.isPending ? 'Reactivating…' : 'Reactivate tenant'}
              </button>
            )
          : canCancel && (
              <button onClick={() => setShowCancel(true)} className="btn-danger">
                Cancel tenant
              </button>
            )}
      </div>

      {showCancel && (
        <CancelTenantModal
          tenantName={tenantName}
          onClose={() => setShowCancel(false)}
          onCancelled={() => {
            setShowCancel(false);
            queryClient.invalidateQueries({ queryKey: ['tenant', 'current'] });
          }}
        />
      )}
    </section>
  );
}

function CancelTenantModal({
  tenantName,
  onClose,
  onCancelled,
}: {
  tenantName: string;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => tenantApi.cancelTenant(),
    onSuccess: onCancelled,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Cancellation failed'),
  });

  const confirmed = typed.trim() === tenantName.trim();

  return (
    <div className="modal-overlay">
      <div className="modal border-danger/40">
        <h2 className="text-lg font-semibold text-danger">Cancel tenant</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This will suspend the workspace and lock out every member. You can reactivate it later
          from this page.
        </p>
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!confirmed) {
              setError('The name you typed does not match.');
              return;
            }
            setError(null);
            mutation.mutate();
          }}
        >
          <label className="block text-sm font-medium">
            Type <span className="font-mono text-foreground">{tenantName}</span> to confirm
          </label>
          <input
            className="input w-full font-mono"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
          />
          {error && (
            <p
              className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger"
              role="alert"
            >
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Keep tenant
            </button>
            <button
              type="submit"
              disabled={!confirmed || mutation.isPending}
              className="btn-danger"
            >
              {mutation.isPending ? 'Cancelling…' : 'Cancel tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
